import { hypotenuse2D } from '@paragon/utilities';

import Direction3D from './Direction3D';
import Point2D, { Point2DInterface } from './Point2D';
import Point3D from './Point3D';
import { angleEquals, convertDegreesToRadians, convertRadiansToDegrees, straightAngle } from './units';
import Vector2D from './Vector2D';

export const DIRECTION_VECTOR_TOLERANCE = 0.00001;

// This is a class that is only to be used by Direction2D and should not be exported.
// If you want a normal vector class, use Vector2D.
class DirectionVector2D {
  static readonly zero = new DirectionVector2D(0, 0);

  readonly x: number;
  readonly y: number;

  get magnitude() {
    return hypotenuse2D(this.x, this.y);
  }

  constructor(x: number, y: number) {
    this.x = x;
    this.y = y;
  }

  normalize() {
    if (this.magnitude < DIRECTION_VECTOR_TOLERANCE) return DirectionVector2D.zero;
    return this.divide(this.magnitude);
  }

  add(vector: DirectionVector2D) {
    return new DirectionVector2D(this.x + vector.x, this.y + vector.y);
  }

  divide(divisor: number) {
    return new DirectionVector2D(this.x / divisor, this.y / divisor);
  }

  // Returns the counter-clockwise angle in degrees from this vector to another vector (in [-180, 180])
  angleTo(vector: DirectionVector2D) {
    const angleInRadians = Math.atan2(vector.y, vector.x) - Math.atan2(this.y, this.x);

    function constrainAngleToRange(angle: number) {
      if (angle > Math.PI) return angle - 2 * Math.PI;
      if (angle < -Math.PI) return angle + 2 * Math.PI;
      return angle;
    }

    return convertRadiansToDegrees(constrainAngleToRange(angleInRadians));
  }

  angleBetween(vector: DirectionVector2D) {
    const dotProduct = this.dotProduct(vector);
    const crossProductMagnitude = this.crossProductMagnitude(vector);
    const angleInRadians = Math.atan2(crossProductMagnitude, dotProduct);
    return convertRadiansToDegrees(angleInRadians);
  }

  dotProduct(vector: DirectionVector2D) {
    return this.x * vector.x + this.y * vector.y;
  }

  crossProductMagnitude(vector: DirectionVector2D) {
    return Math.abs(this.x * vector.y - vector.x * this.y);
  }

  reverse() {
    return new DirectionVector2D(-this.x, -this.y);
  }

  rotate(angle: number) {
    const point = new Point2D(this.x, this.y).rotate(angle);
    return new DirectionVector2D(point.x, point.y);
  }
}

export interface Direction2DInterface {
  readonly x: number;
  readonly y: number;
}

class Direction2D {
  static readonly noDirection = new Direction2D(0, 0);
  static readonly right = new Direction2D(1, 0);
  static readonly left = new Direction2D(-1, 0);
  static readonly up = new Direction2D(0, 1);
  static readonly down = new Direction2D(0, -1);

  readonly x: number;
  readonly y: number;

  private readonly vector: DirectionVector2D;
  private readonly normalized: DirectionVector2D;

  constructor(x: number, y: number) {
    this.vector = new DirectionVector2D(x, y);
    this.normalized = this.vector.normalize();
    this.x = this.normalized.x;
    this.y = this.normalized.y;
  }

  static fromObject(direction: { x: number; y: number }) {
    return new Direction2D(direction.x, direction.y);
  }

  static fromDirectionVector(vector: DirectionVector2D) {
    return new Direction2D(vector.x, vector.y);
  }

  static fromPoint(point: Point2DInterface) {
    return new Direction2D(point.x, point.y);
  }

  static fromPoints(basePoint: Point2D, endPoint: Point2D) {
    return Direction2D.fromPoint(endPoint.subtract(basePoint));
  }

  static fromPointObjects(basePoint: Point2DInterface, endPoint: Point2DInterface) {
    return Direction2D.fromPoints(Point2D.fromObject(basePoint), Point2D.fromObject(endPoint));
  }

  static fromAngle(angle: number) {
    return new Direction2D(Math.cos(convertDegreesToRadians(angle)), Math.sin(convertDegreesToRadians(angle)));
  }

  get angleInRadians() {
    return Math.atan2(this.y, this.x);
  }

  get angle() {
    return convertRadiansToDegrees(this.angleInRadians);
  }

  equals(direction: Direction2D) {
    const hasDirection1 = this.hasDirection();
    const hasDirection2 = direction.hasDirection();
    if (!hasDirection1 && !hasDirection2) return true;
    if (hasDirection1 !== hasDirection2) return false;

    const angleBetween = this.angleBetween(direction);
    return angleEquals(angleBetween, 0);
  }

  hasDirection() {
    return this.vector.magnitude >= DIRECTION_VECTOR_TOLERANCE;
  }

  /**  Returns the counter-clockwise angle in degrees from this direction to another direction (in [-180, 180]) */
  angleTo(direction: Direction2D) {
    return this.normalized.angleTo(direction.normalized);
  }

  angleBetween(direction: Direction2D) {
    return this.normalized.angleBetween(direction.normalized);
  }

  smallestAngleBetween(other: Direction2D) {
    const dotProductMagnitude = Math.abs(this.dotProduct(other));
    if (dotProductMagnitude > 1) return 0;

    return convertRadiansToDegrees(Math.acos(dotProductMagnitude));
  }

  reverse() {
    return Direction2D.fromDirectionVector(this.vector.reverse());
  }

  rotate(angle: number) {
    return Direction2D.fromDirectionVector(this.vector.rotate(angle));
  }

  rotateQuarterTurn() {
    return new Direction2D(-this.y, this.x);
  }

  rotateReverseQuarterTurn() {
    return new Direction2D(this.y, -this.x);
  }

  dotProduct(direction: Direction2D) {
    return this.x * direction.x + this.y * direction.y;
  }

  crossProductMagnitude(direction: Direction2D) {
    return Math.abs(this.x * direction.y - direction.x * this.y);
  }

  isParallelTo(direction: Direction2D) {
    const angle = this.angleBetween(direction);
    return angleEquals(angle, 0) || angleEquals(angle, straightAngle);
  }

  isPerpendicularTo(direction: Direction2D) {
    const angle = this.angleBetween(direction);
    return angleEquals(Math.abs(angle), 90);
  }

  isVertical() {
    return this.isParallelTo(Direction2D.up);
  }

  isHorizontal() {
    return this.isParallelTo(Direction2D.right);
  }

  isOrientedWith(direction: Direction2D) {
    return this.dotProduct(direction) >= 0;
  }

  orientWith(direction: Direction2D) {
    return this.isOrientedWith(direction) ? this : this.reverse();
  }

  toVector(scale: number) {
    return new Vector2D(scale * this.x, scale * this.y);
  }

  toObject(): Direction2DInterface {
    return { x: this.x, y: this.y };
  }

  to3D() {
    return Direction3D.fromOriginToPoint(new Point3D(this.x, this.y, 0));
  }

  static bisect(direction1: Direction2D, direction2: Direction2D): Direction2D {
    return Direction2D.fromDirectionVector(direction1.normalized.add(direction2.normalized));
  }
}

export default Direction2D;
